import React, { useEffect } from 'react'
import { Link, useLocation } from 'react-router'
import clsx from 'clsx'
import { Divider, ScrollArea, Stack } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import {
  IconAward,
  IconBolt,
  IconGitCompare,
  IconHelmet,
  IconHome,
  IconListNumbers,
  IconNews,
  IconRocket,
  IconScoreboard,
  IconTrophy,
  IconUser,
  IconUsers,
} from '@tabler/icons-react'
import { ReactComponent as Clair } from '../assets/clair.svg'
import { User } from '../auth/api'
import classes from './Navigation.module.css'
import { EditModeSwitch } from '../widgets/providers'
import { useIsAdmin } from '../widgets/api'
import CompetitionSelect from './CompetitionSelect'
import { Typography } from '../common/typography'
import { useContentPages } from '../content_pages/api'
import { useCompetitionId } from '../common/hooks'
import { useActiveCompetitions, useCompetitionDetails } from './api'
import NavigationFooter from './NavigationFooter'
import NavigationHeader from './NavigationHeader'
import NavigationMobile from './NavigationMobile'
import NavigationDesktop from './NavigationDesktop'

const pages = [
  {
    label: 'Home',
    link: '/home',
    icon: IconHome,
    isCompetition: true,
    requiredFeature: null,
  },
  {
    label: 'News',
    link: '/news',
    icon: IconNews,
    isCompetition: false,
    requiredFeature: null,
  },
  {
    label: 'Arena',
    link: '/arena',
    icon: IconAward,
    isCompetition: true,
    requiredFeature: 'has_arena' as const,
  },
  {
    label: 'Players',
    link: '/players',
    icon: IconUser,
    isCompetition: true,
    requiredFeature: null,
  },
  {
    label: 'Teams',
    link: '/teams',
    icon: IconUsers,
    isCompetition: true,
    requiredFeature: null,
  },
  {
    label: 'League Ladder',
    link: '/ladder',
    icon: IconListNumbers,
    isCompetition: true,
    requiredFeature: null,
  },
  {
    label: 'Overall Rank',
    link: '/ranks',
    icon: IconTrophy,
    isCompetition: true,
    requiredFeature: null,
  },
  {
    label: 'Performance',
    link: '/performance',
    icon: IconRocket,
    isCompetition: true,
    requiredFeature: null,
  },
  {
    label: 'Compare',
    link: '/compare',
    icon: IconGitCompare,
    isCompetition: true,
    requiredFeature: null,
  },
  {
    label: 'Learning XP',
    link: '/learning',
    icon: IconBolt,
    isCompetition: false,
    requiredFeature: 'has_learning_module' as const,
  },
  {
    label: 'Ask Clair',
    link: '/clair',
    icon: Clair,
    isCompetition: false,
    requiredFeature: 'has_clair_ai' as const,
  },
]

interface SubpageProps {
  label: string
  link: string
  setMobileNavigationOpen: React.Dispatch<React.SetStateAction<boolean>>
  location_pathname: string
}

function Subpage({
  label,
  link,
  setMobileNavigationOpen,
  location_pathname,
}: SubpageProps) {
  const isActive = location_pathname
    .toLowerCase()
    .startsWith(link.toLowerCase())

  const fc = isActive
    ? { light: 'primary-6', dark: 'primary-3' }
    : { light: 'gray-9', dark: 'gray-3' }

  return (
    <>
      <Link
        to={link}
        className={clsx(classes.link, classes.specialRow, {
          [classes.active]: isActive,
        })}
        onClick={() => setTimeout(() => setMobileNavigationOpen(false), 100)}
      >
        <Typography
          className={clsx({ [classes.active]: isActive })}
          fs="sm"
          fc={fc}
          hc={{ light: 'primary-6', dark: 'primary-3' }}
        >
          {label}
        </Typography>
      </Link>
      <Divider className={classes.divider} />
    </>
  )
}

function ContentPages({
  setMobileNavigationOpen,
}: {
  setMobileNavigationOpen: React.Dispatch<React.SetStateAction<boolean>>
}) {
  const { pathname } = useLocation()
  const competition_id = useCompetitionId()

  const { data: contentPages } = useContentPages({ competition_id })

  const subpages = contentPages.map((page) => ({
    label: page.title,
    link: `/page/${page.slug}`,
  }))

  subpages.push({
    label: 'Settings',
    link: '/settings',
  })

  return subpages.map((item) => (
    <Subpage
      {...item}
      key={item.label}
      setMobileNavigationOpen={setMobileNavigationOpen}
      location_pathname={pathname}
    />
  ))
}

interface Props {
  user: User
  mobileNavigationOpen: boolean
  setMobileNavigationOpen: React.Dispatch<React.SetStateAction<boolean>>
}

export default function Navigation({
  user,
  mobileNavigationOpen,
  setMobileNavigationOpen,
}: Props) {
  const competition_id = useCompetitionId()
  const isMobile = useMediaQuery('(max-width: 1200px)')
  const { data: adminCheck } = useIsAdmin()
  const { data: activeCompetitionsData } = useActiveCompetitions()
  const platformFeatures = activeCompetitionsData?.platform_features

  useEffect(() => {
    if (isMobile && mobileNavigationOpen) {
      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
    } else {
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
    }

    return () => {
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
    }
  }, [isMobile, mobileNavigationOpen])

  const competitionDetails = useCompetitionDetails({
    competition_id: competition_id,
  })

  const pagesWithCompetitionType = [...pages]
    .filter((item) => {
      if (
        competitionDetails?.data?.single_player_teams &&
        item.link === '/players'
      ) {
        return false
      }
      return true
    })
    .map((item) => {
      if (
        competitionDetails?.data?.single_player_teams &&
        item.link === '/teams'
      ) {
        return { ...item, label: 'Players' }
      }
      return item
    })
  if (competitionDetails) {
    const insertIndex = pagesWithCompetitionType.findIndex(
      (p) => p.link === '/ladder'
    )
    if (competitionDetails.data?.kind === 'match') {
      pagesWithCompetitionType.splice(insertIndex, 0, {
        label: 'Matches',
        link: '/matches',
        icon: IconScoreboard,
        isCompetition: true,
        requiredFeature: null,
      })
    } else if (competitionDetails.data?.kind === 'race') {
      pagesWithCompetitionType.splice(insertIndex, 0, {
        label: 'Races',
        link: '/races',
        icon: IconHelmet,
        isCompetition: true,
        requiredFeature: null,
      })
    }
  }

  const filteredPages = pagesWithCompetitionType.filter((item) => {
    if (platformFeatures && item.requiredFeature) {
      return !!platformFeatures[
        item.requiredFeature as keyof typeof platformFeatures
      ]
    }
    return true
  })

  const sitePages = (
    <ContentPages setMobileNavigationOpen={setMobileNavigationOpen} />
  )

  return (
    <nav
      className={clsx(isMobile ? classes.mobileContainer : classes.container, {
        [classes.mobileContainerOpen]: mobileNavigationOpen && isMobile,
      })}
    >
      <NavigationHeader
        user={`${user.first_name} ${user.last_name}`}
        isMobile={isMobile}
        setMobileNavigationOpen={setMobileNavigationOpen}
      />
      <div className={classes.competitionPicker} data-mobile={isMobile}>
        <CompetitionSelect
          isMobile={isMobile}
          setMobileNavigationOpen={setMobileNavigationOpen}
        />
      </div>
      <Stack className={classes.navigationMain} data-mobile={isMobile}>
        <ScrollArea scrollbars="y">
          <div
            className={
              isMobile
                ? classes.navigationMainMobile
                : classes.navigationMainDesktop
            }
          >
            {isMobile ? (
              <NavigationMobile
                pagesWithCompetitionType={filteredPages}
                setMobileNavigationOpen={setMobileNavigationOpen}
                competition_id={competition_id}
              />
            ) : (
              <NavigationDesktop
                pagesWithCompetitionType={filteredPages}
                competition_id={competition_id}
                isAdmin={adminCheck.is_admin}
              />
            )}
            <div className={classes.linksContainer}>{sitePages}</div>
            {adminCheck?.is_admin && <EditModeSwitch />}
          </div>
        </ScrollArea>
      </Stack>
      <NavigationFooter isMobile={isMobile} user={user} />
    </nav>
  )
}
